import { makeAutoObservable } from "mobx";
import { ApiService, downloadFile, getFilenameFromHeaders, isAxiosError } from "../../../api";
import { AxiosInstance } from "axios";
import {
  GetSubscriptionFormSigningStatusResponse,
  mapToSignSubscriptionFormShares,
  SignSubscriptionForm,
  SubscriptionFormDialogState,
  SubscriptionModel,
} from "./subscriptionFormDomain";
import {
  noDialog,
  ViewDocumentSigningStatusDialogModel,
} from "../../../dbot/summary/summaryDomain";
import { ok } from "../../../common/common";
import AlertMessageStore from "../../../stores/AlertMessageStore";
import { DocumentType } from "../../../common/documents";

export class SubscriptionFormSignStore {
  readonly http: AxiosInstance;
  readonly apiService: ApiService;
  readonly documentReference: string | null;
  document: SignSubscriptionForm | null = null;
  subscription: SubscriptionModel | null = null;
  state:
    | "loading"
    | "documentFetched"
    | "fetchedError"
    | "documentRead"
    | "signingInProgress"
    | "documentSigned"
    | "electronicSubscriptionForm"
    | "subscriptionSubmitError" = "loading";
  downloadResult: "ok" | "pending" = "ok";
  submitResult: "ok" | "pending" = "ok";
  dialogState: SubscriptionFormDialogState;
  messageStore: AlertMessageStore;

  constructor(
    messageStore: AlertMessageStore,
    apiService: ApiService,
    http: AxiosInstance,
    documentReference: string | null,
  ) {
    this.messageStore = messageStore;
    this.http = http;
    this.apiService = apiService;
    this.documentReference = documentReference;
    this.dialogState = noDialog();
    makeAutoObservable(this, { http: false, apiService: false }, { autoBind: true });
  }

  get isDownloadingDocument(): boolean {
    return this.downloadResult === "pending";
  }

  get isSubmittingSubscriptionForm() {
    return this.submitResult === "pending";
  }

  *fetchDocumentDetails() {
    try {
      if (!this.documentReference) return;
      this.document = yield this.apiService.getSubscriptionFormDetails(this.documentReference);
      if (this.document?.isSigned === true) {
        this.state = "documentSigned";
      } else {
        this.state = "documentFetched";
      }
    } catch (e) {
      this.state = "fetchedError";
    } finally {
      if (this.document) {
        this.subscription = new SubscriptionModel(this.document);
      }
    }
  }

  *fetchDocumentSigningStatus() {
    try {
      if (!this.documentReference || !this.subscription) return;
      const data: GetSubscriptionFormSigningStatusResponse =
        yield this.apiService.getSubscriptionFormSigningStatus(this.documentReference);
      this.subscription.setSigners(data.signingStatus.signerStatus);
      this.subscription.setShares(data.shares);
      if (this.dialogState.type === "viewSigningStatus") {
        this.dialogState.model.setSigningStatusResult(ok(data.signingStatus));
      }
    } catch (e) {
      this.state = "fetchedError";
    }
  }

  openSigningStatusDialog() {
    if (this.document === null) return;
    this.dialogState = {
      type: "viewSigningStatus",
      model: new ViewDocumentSigningStatusDialogModel({
        documentType: DocumentType.subscription_form,
        title: {
          key: "signing.subscription.signingStatus.title",
          params: { name: this.subscription?.subscriber.name },
        },
        getSigningStatus: () => this.fetchDocumentSigningStatus(),
        closeDialog: this.closeDialog,
      }),
    };
    this.dialogState.model.init();
  }

  closeDialog() {
    this.dialogState = { type: "none" };
  }

  submitSubscriptionFormDetails() {
    if (!this.documentReference || !this.document) return Promise.reject();
    if (this.subscription?.validate()) {
      this.submitResult = "pending";
      return this.apiService
        .updateSubscriptionFormDetails(
          this.documentReference,
          mapToSignSubscriptionFormShares(this.subscription),
        )
        .then(() => {
          this.state = "signingInProgress";
        })
        .catch((e) => {
          if (isAxiosError(e) && e.response?.data) {
            this.messageStore.showError({
              key: "signing.subscription.form.error.invalidSubscriptionForm",
            });
            this.fetchDocumentSigningStatus();
          } else {
            this.state = "subscriptionSubmitError";
            return Promise.reject(e);
          }
        })
        .finally(() => {
          this.submitResult = "ok";
        });
    } else {
      return Promise.reject();
    }
  }

  setElectronicSubscriptionForm() {
    this.state = "electronicSubscriptionForm";
  }

  setDocumentRead() {
    this.state = "documentRead";
  }

  download() {
    this.downloadResult = "pending";
    this.http
      .get(`/sign/download-document-for-signer/${this.documentReference}`, {
        responseType: "blob",
      })
      .then((res) => {
        downloadFile(res.data, getFilenameFromHeaders(res.headers));
      })
      .catch(() => {
        this.messageStore.showError({ key: "signing.download.failed" });
      })
      .finally(() => {
        this.downloadResult = "ok";
      });
  }
}
